package co.uk.sacursoftware.ssinventory.util

import androidx.compose.ui.graphics.ImageBitmap
import io.ktor.client.HttpClient
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.request.get
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.readRawBytes
import io.ktor.http.isSuccess
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.coroutines.cancellation.CancellationException
import org.jetbrains.compose.resources.decodeToImageBitmap

private val uuidRegex = Regex(
    "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}"
)

internal fun imageCacheKeys(url: String, imageId: String?): List<String> {
    val keys = linkedSetOf<String>()
    if (!imageId.isNullOrBlank()) keys += imageId.lowercase()
    uuidRegex.find(url)?.value?.lowercase()?.let { keys += it }
    keys += urlToStableKey(url)
    return keys.toList()
}

private fun urlToStableKey(url: String): String {
    val path = url.substringAfter("/o/", missingDelimiterValue = "")
        .substringBefore("?")
        .ifBlank { url.substringBefore("?") }
        .replace("%2F", "/", ignoreCase = true)
        .substringAfterLast('/')
        .ifBlank { url }
    return path.replace(Regex("[^A-Za-z0-9._-]"), "_").take(180)
}

object ImageCache {
    private const val maxMemoryEntries = 80
    private val memory = LinkedHashMap<String, ImageBitmap>(16, 0.75f)
    private val inflight = HashMap<String, CompletableDeferred<ImageBitmap>>()
    private val mutex = Mutex()
    private val disk by lazy { ImageByteStore() }
    private val http by lazy {
        HttpClient {
            expectSuccess = false
            install(HttpTimeout) {
                requestTimeoutMillis = 40_000
                connectTimeoutMillis = 40_000
                socketTimeoutMillis = 40_000
            }
        }
    }

    fun peek(url: String, imageId: String? = null): ImageBitmap? {
        val keys = imageCacheKeys(url, imageId)
        for (key in keys) {
            val hit = memory[key]
            if (hit != null) return hit
        }
        return null
    }

    suspend fun load(url: String, imageId: String? = null): ImageBitmap {
        peek(url, imageId)?.let { return it }
        val keys = imageCacheKeys(url, imageId)
        val primary = keys.first()
        var created = false
        val deferred = mutex.withLock {
            inflight[primary] ?: CompletableDeferred<ImageBitmap>().also {
                inflight[primary] = it
                created = true
            }
        }
        if (!created) return deferred.await()

        try {
            peek(url, imageId)?.let {
                deferred.complete(it)
                return it
            }
            val fromDisk = keys.firstNotNullOfOrNull { disk.getBytes(it) }
            val bytes = fromDisk ?: download(url)
            if (fromDisk == null) {
                keys.forEach { disk.putBytes(it, bytes) }
            }
            val bitmap = bytes.decodeToImageBitmap()
            mutex.withLock { putMemory(keys, bitmap) }
            deferred.complete(bitmap)
            return bitmap
        } catch (e: CancellationException) {
            deferred.cancel()
            throw e
        } catch (e: Throwable) {
            deferred.completeExceptionally(e)
            throw e
        } finally {
            mutex.withLock { inflight.remove(primary) }
        }
    }

    suspend fun evict(url: String, imageId: String? = null) {
        val keys = imageCacheKeys(url, imageId)
        mutex.withLock {
            keys.forEach { memory.remove(it) }
        }
        keys.forEach { disk.removeBytes(it) }
    }

    private suspend fun download(url: String): ByteArray {
        return try {
            val response: HttpResponse = http.get(url)
            if (!response.status.isSuccess()) {
                throw IllegalStateException("Image download failed: HTTP ${response.status.value}")
            }
            response.readRawBytes()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            throw Exception("Image download failed: ${e.message}", e)
        }
    }

    private fun putMemory(keys: List<String>, bitmap: ImageBitmap) {
        keys.forEach { key ->
            memory.remove(key)
            memory[key] = bitmap
        }
        while (memory.size > maxMemoryEntries) {
            val eldest = memory.keys.firstOrNull() ?: break
            memory.remove(eldest)
        }
    }
}
